
import React, { useState, useEffect, useMemo } from 'react';
import { BookOpen, Check, XCircle, AlertCircle } from 'lucide-react';
import styles from '../CSS/CreateRoleForm.module.css';

const ClassGroupPermissionSelector = ({ 
  selectedGroups = [],
  onChange 
}) => {
  const predefinedGroups = [
    { id: 'DBH', name: 'DBH', icon: '📚', description: 'Grupos de educación secundaria' },
    { id: 'LH', name: 'LH', icon: '🎓', description: 'Grupos de educación primaria' },
    { id: 'Botiquin', name: 'Botiquín', icon: '🏥', description: 'Dispositivos de botiquín' }
  ];

  const getInitialMode = useMemo(() => {
    if (selectedGroups.length === 0) return 'none';
    if (selectedGroups.length === predefinedGroups.length) return 'all';
    return 'specific';
  }, [selectedGroups.length]);
  
  const [selectionMode, setSelectionMode] = useState(getInitialMode);

  useEffect(() => {
    setSelectionMode(prevMode => {
      if (prevMode !== getInitialMode) {
        return getInitialMode;
      }
      return prevMode;
    });
  }, [getInitialMode]);

  const handleModeChange = (mode) => {
    setSelectionMode(mode);
    if (mode === 'all') {
      onChange(predefinedGroups.map(g => g.id));
    } else if (mode === 'none') {
      onChange([]);
    }
  };

  const isGroupSelected = (groupId) => {
    return selectedGroups.includes(groupId);
  };

  const toggleGroupSelection = (groupId) => {
    if (selectionMode !== 'specific') {
      setSelectionMode('specific');
    }

    const newSelected = isGroupSelected(groupId)
      ? selectedGroups.filter(id => id !== groupId)
      : [...selectedGroups, groupId];
    
    onChange(newSelected);
  };

  return (
    <div className={styles.classGroupSelectorContainer}>
      <div className={styles.classGroupSelectorHeader}>
        <h4 className={styles.classGroupSelectorTitle}>
          <BookOpen size={18} />
          Grupos de Clases Permitidos
        </h4>
        <p className={styles.classGroupSelectorDescription}>
          Selecciona los grupos predefinidos a los que este rol tendrá acceso en el módulo de Clases
        </p>
      </div>

      <div className={styles.classGroupSelectionModes}>
        <button
          onClick={() => handleModeChange('all')}
          className={`${styles.classGroupModeButton} ${selectionMode === 'all' ? styles.classGroupModeActive : ''}`}
        >
          <Check size={14} />
          Todos los Grupos
        </button>
        
        <button
          onClick={() => handleModeChange('specific')}
          className={`${styles.classGroupModeButton} ${selectionMode === 'specific' ? styles.classGroupModeActive : ''}`}
        >
          <BookOpen size={14} />
          Específicos
        </button>
        
        <button
          onClick={() => handleModeChange('none')}
          className={`${styles.classGroupModeButton} ${selectionMode === 'none' ? styles.classGroupModeActive : ''}`}
        >
          <XCircle size={14} />
          Ninguno
        </button>
      </div>

      {selectionMode === 'specific' && (
        <>
          <div className={styles.classGroupSelectedInfo}>
            <AlertCircle size={14} />
            <span>{selectedGroups.length} grupos seleccionados</span>
          </div>

          <div className={styles.classGroupGridContainer}>
            {predefinedGroups.map(group => {
              const isSelected = isGroupSelected(group.id);
              
              return (
                <button
                  key={group.id}
                  onClick={() => toggleGroupSelection(group.id)}
                  className={`${styles.classGroupCard} ${isSelected ? styles.classGroupSelected : ''}`}
                >
                  <div className={styles.classGroupCheckbox}>
                    {isSelected && <Check size={14} />}
                  </div>
                  
                  <div className={styles.classGroupIcon}>
                    {group.icon}
                  </div>
                  
                  <div className={styles.classGroupInfo}>
                    <span className={styles.classGroupName}>{group.name}</span>
                    <span className={styles.classGroupDescription}>{group.description}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {selectionMode === 'all' && (
        <div className={styles.classGroupInfoBox}>
          <Check size={16} />
          <span>Este rol tendrá acceso a todos los grupos predefinidos de clases</span>
        </div>
      )}

      {selectionMode === 'none' && (
        <div className={styles.classGroupWarningBox}>
          <AlertCircle size={16} />
          <span>Este rol no tendrá acceso a ningún grupo de clases</span>
        </div>
      )}
    </div>
  );
};

export default ClassGroupPermissionSelector;